import { UserContext } from "@context/User/UserContext";
import { getLngCode } from "@i18n/i18n";
import { Box, Grid } from "@mui/material";
import { chatService } from "@services/chatBackend";
import { useChatThreadMessages, useChatThreads } from "@services/chatHistory";
import { useContext, useEffect, useMemo, useState } from "react";

import AvatarChatBox from "./AvatarChatBox";
import ChatHistorySidebar from "./ChatHistorySidebar";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import {
  AvatarChatHistoryMessageSource,
  IAvatarChatHistoryMessage
} from "./AvatarChatHistory";
import { ModalSummary } from "./ModalSummary";

const createInitialHistory = (
  welcomeText: string
): IAvatarChatHistoryMessage[] => [
  {
    source: AvatarChatHistoryMessageSource.AVATAR,
    text: welcomeText,
    timestamp: new Date()
  }
];

export const Chat = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(UserContext);
  const [sessionId, setSessionId] = useState<string>(uuidv4());
  const { keycloak } = useKeycloak();

  const [isLoadingRepeat, setIsLoadingRepeat] = useState(false);
  const [text, setText] = useState<string>("");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [openSummary, setOpenSummary] = useState(false);
  const userName =
    keycloak.tokenParsed?.name || keycloak.tokenParsed?.email || "User";

  const [chatHistory, setChatHistory] =
    useState<IAvatarChatHistoryMessage[]>(
      createInitialHistory(t("chatbot.common.welcome_message", { name: userName }))
    );
  const [isLoading, setIsLoading] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [autoScrollBottom, setAutoScrollBottom] = useState(true);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);

  const {
    data: threadsPages,
    fetchNextPage: fetchNextThreadsPage,
    hasNextPage: hasMoreThreads,
    isFetchingNextPage: isFetchingNextThreads
  } = useChatThreads();

  const {
    data: threadMessagesPages,
    fetchNextPage: fetchNextMessagesPage,
    hasNextPage: hasMoreMessages,
    isFetchingNextPage: isFetchingNextMessages
  } = useChatThreadMessages(activeThreadId);

  const threads = useMemo(
    () =>
      threadsPages?.pages.flatMap((page) =>
        page.threads.map((t) => ({
          id: t.id,
          title: t.title,
          createdAt: t.createdAt,
          updatedAt: t.updatedAt
        }))
      ) ?? [],
    [threadsPages]
  );

  useEffect(() => {
    if (!threadMessagesPages) return;

    const allMessages = threadMessagesPages.pages.flatMap(
      (page) => page.messages
    );

    // Los mensajes vienen más recientes primero; los invertimos para el chat
    const sorted = [...allMessages].reverse();

    const mapped: IAvatarChatHistoryMessage[] = sorted.map((msg) => ({
      source:
        (msg.role as string) === "user"
          ? AvatarChatHistoryMessageSource.USER
          : AvatarChatHistoryMessageSource.AVATAR,
      text: (msg.content as string) ?? "",
      timestamp: new Date(msg.timestamp as string)
    }));

    setChatHistory(
      mapped.length > 0
        ? mapped
        : createInitialHistory(
            t("chatbot.common.welcome_message", { name: userName })
          )
    );
  }, [threadMessagesPages, t, userName]);

  const handleRecord = (blob: Blob) => {
    setAudioBlob(blob);
  };

  const handleDeleteAudio = () => {
    setAudioBlob(null);
  };

  const addMessageToHistory = (
    messageText: string,
    source: AvatarChatHistoryMessageSource
  ) => {
    const message: IAvatarChatHistoryMessage = {
      source,
      text: messageText,
      timestamp: new Date()
    };
    setChatHistory((prev) => [...prev, message]);
  };

  const updateLastBotMessage = (messageText: string) => {
    setChatHistory((prev) => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      if (
        lastIndex >= 0 &&
        updated[lastIndex].source === AvatarChatHistoryMessageSource.AVATAR
      ) {
        updated[lastIndex] = {
          ...updated[lastIndex],
          text: messageText
        };
      }
      return updated;
    });
  };

  const updateTranscriptionInHistory = (transcription: string) => {
    setChatHistory((prev) => {
      const updated = [...prev];

      let userMessageIndex = -1;
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].source === AvatarChatHistoryMessageSource.USER) {
          userMessageIndex = i;
          break;
        }
      }

      if (userMessageIndex >= 0) {
        updated[userMessageIndex] = {
          ...updated[userMessageIndex],
          text: transcription || t("chatbot.common.transcription_error")
        };
      }

      return updated;
    });
  };

  const createStreamCallbacks = () => ({
    onToken: (token: string) => {
      setChatHistory((prev) => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (
          lastIndex >= 0 &&
          updated[lastIndex].source === AvatarChatHistoryMessageSource.AVATAR
        ) {
          const currentText = updated[lastIndex].text || "";
          const thinkingText = t("chatbot.actions.thinking");
          const newText =
            currentText === thinkingText || currentText === ""
              ? token
              : currentText + token;
          updated[lastIndex] = {
            ...updated[lastIndex],
            text: newText
          };
        }
        return updated;
      });
    },
    onTranscription: (transcription: string) => {
      updateTranscriptionInHistory(transcription);
    },
    onError: (_error: string) => {
      updateLastBotMessage(t("chatbot.errors.connection_error"));
    }
  });

  const streamResponse = async (streamPromise: Promise<void>) => {
    setIsLoading(true);
    addMessageToHistory(
      t("chatbot.actions.thinking"),
      AvatarChatHistoryMessageSource.AVATAR
    );
    try {
      await streamPromise;
    } catch (error) {
      console.error("Error durante el streaming:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVoiceMessage = async (): Promise<void> => {
    if (!audioBlob || !sessionId) return;

    addMessageToHistory(
      t("chatbot.common.voice_record"),
      AvatarChatHistoryMessageSource.USER
    );

    await streamResponse(
      chatService.streamChatRequestWithAudioOrText(
        sessionId,
        getLngCode(currentUser.language),
        undefined,
        audioBlob,
        createStreamCallbacks()
      )
    );

    handleDeleteAudio();
  };

  const handleTextMessage = async (): Promise<void> => {
    if (!text || !sessionId) return;

    const currentText = text;
    addMessageToHistory(currentText, AvatarChatHistoryMessageSource.USER);
    setText("");

    await streamResponse(
      chatService.streamChatRequestWithAudioOrText(
        sessionId,
        getLngCode(currentUser.language),
        currentText,
        undefined,
        createStreamCallbacks()
      )
    );
  };

  async function handleSpeak() {
    setIsLoadingRepeat(true);

    if (audioBlob) {
      await handleVoiceMessage();
    } else {
      await handleTextMessage();
    }

    setIsLoadingRepeat(false);
  }

  const handleNewConversation = () => {
    setActiveThreadId(null);
    setSessionId(uuidv4());
    setAutoScrollBottom(true);
    setChatHistory(
      createInitialHistory(
        t("chatbot.common.welcome_message", { name: userName })
      )
    );
  };

  const handleSelectThread = (threadId: string) => {
    setAutoScrollBottom(true);
    // Aseguramos que el sessionId coincida con el thread seleccionado
    setSessionId(threadId);
    setActiveThreadId(threadId);
  };

  const handleMessagesScroll = async (
    event: React.UIEvent<HTMLDivElement, UIEvent>
  ) => {
    const target = event.currentTarget;
    // Si el usuario está cerca de la parte superior, cargamos más mensajes antiguos
    if (
      hasMoreMessages &&
      !isFetchingNextMessages &&
      !isLoadingMoreMessages &&
      target.scrollTop <= 10
    ) {
      setAutoScrollBottom(false);
      setIsLoadingMoreMessages(true);
      try {
        await fetchNextMessagesPage();
      } finally {
        setIsLoadingMoreMessages(false);
      }
    }
    // Si el usuario vuelve a la parte inferior, reactivamos el auto-scroll
    if (
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - 10
    ) {
      setAutoScrollBottom(true);
    }
  };

  const handleThreadsScroll = (
    event: React.UIEvent<HTMLUListElement, UIEvent>
  ) => {
    const target = event.currentTarget;
    if (
      hasMoreThreads &&
      target.scrollTop + target.clientHeight >= target.scrollHeight - 10
    ) {
      fetchNextThreadsPage();
    }
  };

  const handleOpenSummary = () => {
    setOpenSummary(true);
  };

  const handleCloseSummary = () => {
    setOpenSummary(false);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
      <Grid
        container
        spacing={2}
        display="flex"
        justifyContent="center"
        alignContent="flex-end"
        alignItems="flex-end"
        sx={{ height: "100%", width: "100%", marginTop: 7 }}
      >
        <Grid item xs={12} md={3} height={"64vh"}>
          <ChatHistorySidebar
            threads={threads}
            activeThreadId={activeThreadId}
            hasMoreThreads={Boolean(hasMoreThreads)}
            isFetchingNextThreads={isFetchingNextThreads}
            onNewConversation={handleNewConversation}
            onSelectThread={handleSelectThread}
            onLoadMoreThreads={() => fetchNextThreadsPage()}
            onScroll={handleThreadsScroll}
          />
        </Grid>
        <Grid item xs={12} md={9} height={"64vh"}>
          <AvatarChatBox
            messages={chatHistory}
            disabled={isLoading}
            loading={isLoadingRepeat}
            text={text}
            setText={setText}
            handleSpeak={handleSpeak}
            handleOpenSummary={handleOpenSummary}
            handleRecord={handleRecord}
            audioBlob={audioBlob}
            handleDeleteAudio={handleDeleteAudio}
            onScrollHistory={handleMessagesScroll}
            autoScrollToBottom={autoScrollBottom}
            isHistoryLoading={isFetchingNextMessages || isLoadingMoreMessages}
          />
        </Grid>
      </Grid>

      {openSummary && (
        <ModalSummary
          open={openSummary}
          onClose={handleCloseSummary}
          sessionId={sessionId}
        />
      )}
    </Box>
  );
};

export default Chat;
